import { useState } from "react";
import { toast } from "sonner";
import { CheckCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatError } from "@/lib/api";

const ACTIONS = {
  arts: { title: "Apagar as artes deste pedido?", desc: "Os arquivos de arte deixam de ficar disponíveis para download. O pedido continua no histórico.", done: "Artes apagadas" },
  order: { title: "Apagar o pedido inteiro?", desc: "O pedido e as artes somem do painel. O valor continua contando no faturamento. Essa ação não pode ser desfeita.", done: "Pedido apagado" },
};

export function OrderDeleteActions({ order, onUpdated, onDeleted }) {
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const a = confirm && ACTIONS[confirm];
  const notified = !!order.notified_at;

  const markNotified = async () => {
    try {
      onUpdated((await api.post(`/admin/orders/${order.id}/notified`)).data);
      toast.success(`Pedido #${order.number} marcado como avisado`);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      if (confirm === "arts") onUpdated((await api.delete(`/admin/orders/${order.id}/arts`)).data);
      else {
        await api.delete(`/admin/orders/${order.id}`);
        onDeleted(order);
      }
      toast.success(`${a.done} · #${order.number}`);
    } catch (e) {
      toast.error(formatError(e));
    }
    setBusy(false);
    setConfirm(null);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-100 bg-red-50/60 p-3" data-testid="order-delete-actions">
      {notified ? (
        <p className="text-xs font-semibold text-red-900">Pedido pronto e cliente avisado: você já pode apagar.</p>
      ) : (
        <div className="space-y-2" data-testid="delete-locked-hint">
          <p className="text-xs font-semibold text-red-900">Para apagar, avise o cliente pelo botão do WhatsApp acima ou marque como avisado.</p>
          <Button variant="outline" size="sm" onClick={markNotified} className="border-red-200 text-red-700 hover:bg-red-100" data-testid="btn-mark-notified"><CheckCheck className="h-3.5 w-3.5" /> Já avisei o cliente</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" disabled={!notified || !order.art_files?.length} onClick={() => setConfirm("arts")} className="border-red-200 text-red-700 hover:bg-red-100" data-testid="btn-delete-arts"><Trash2 className="h-3.5 w-3.5" /> Apagar só as artes</Button>
        <Button size="sm" disabled={!notified} onClick={() => setConfirm("order")} className="bg-red-600 hover:bg-red-700" data-testid="btn-delete-order"><Trash2 className="h-3.5 w-3.5" /> Apagar pedido inteiro</Button>
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{a?.title}</AlertDialogTitle>
            <AlertDialogDescription>Pedido #{order.number} · {order.customer.name}. {a?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">{busy ? "Apagando..." : "Apagar"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
